import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    ViewEncapsulation,
} from "@angular/core";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { AcademicGrid } from "./s25.academic.grid";
import { AcademicGridStore } from "./s25.academic.grid.store";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25ItemComponent } from "../s25-item/s25.item.component";
import { Item } from "../../pojo/Item";
import { UserStore } from "../../stores/user.store";
import { OccurrenceTimeRangeLabels } from "../../util/occurrence.time.colors";
import { S25Date } from "../../util/core/s25.date";

@Component({
    standalone: true,
    selector: "s25-ng-academic-grid-item-tooltip",
    template: `
        <div class="header">
            <a s25-ng-router-link [stateName]="'event'" [stateParams]="{ itemId: data().eventId }">
                {{ data().eventName }}
            </a>
            <s25-ng-icon [type]="'event'" [ariaLabel]="false" />
        </div>

        @if (data().eventTitle; as title) {
            <p>{{ title }}</p>
        }

        @if (hasSortByRelevance()) {
            <s25-ng-button class="sortByRelevance" [type]="'primary'" (clicked)="toggleSortByRelevance()">
                {{ sortedByRelevance() ? "Cancel Sort" : "Sort Locations by Relevance" }}
            </s25-ng-button>
        }

        <dl>
            <dt>Time:</dt>
            <dd>{{ eventTime() }}</dd>

            @if (additionalTime().length) {
                <dt>Additional Time:</dt>
                <dd class="additional-time">
                    <dl>
                        @for (band of additionalTime(); track band.label) {
                            <dt>{{ band.label }}:</dt>
                            <dd>{{ band.duration }}</dd>
                        }
                    </dl>
                </dd>
            }

            @if (data().primaryOrganization; as primaryOrg) {
                <dt>Organization:</dt>
                <dd>
                    <s25-item
                        [itemTypeId]="Item.Ids.Organization"
                        [itemId]="primaryOrg.id"
                        [itemName]="primaryOrg.name"
                    />
                </dd>
            }

            <dt>Head Count:</dt>
            <dd class="headcounts">
                <dl>
                    <dt>Expected:</dt>
                    <dd>{{ data().expectedHeadCount ?? 0 }}</dd>
                    <dt>Registered:</dt>
                    <dd>{{ data().registeredHeadCount ?? 0 }}</dd>
                </dl>
            </dd>

            @if (data().featurePreferences.length) {
                <dt>Feature Preferences:</dt>
                <dd>
                    <ul>
                        @for (pref of data().featurePreferences; track pref.id) {
                            <li>{{ pref.name }}</li>
                        }
                    </ul>
                </dd>
            }

            @if (data().current().instructors.length) {
                <dt>Instructor:</dt>
                <dd>
                    @let instructor = data().current().instructors[0];
                    <s25-item [itemTypeId]="Item.Ids.Contact" [itemId]="instructor.id" [itemName]="instructor.name" />
                </dd>
            }

            @if (data().current().locations.length) {
                <dt>Locations:</dt>
                <dd>
                    <ul>
                        @for (location of data().current().locations; track location.id) {
                            <li>
                                <s25-item
                                    [itemTypeId]="Item.Ids.Location"
                                    [itemId]="location.id"
                                    [itemName]="location.name"
                                />
                            </li>
                        }
                    </ul>
                </dd>
            }

            @if (data().partitionPreferences.length) {
                <dt>Section Partition Preferences:</dt>
                <dd>
                    <ul>
                        @for (pref of data().partitionPreferences; track pref.id) {
                            <li>{{ pref.name }}</li>
                        }
                    </ul>
                </dd>
            }

            @if (data().primaryOrganization?.partitionPreferences?.length) {
                <dt>Organization Partition Preferences:</dt>
                <dd>
                    <ul class="partitionPriority">
                        @for (priority of data().primaryOrganization!.partitionPreferences; track $index) {
                            <li>
                                <strong>Priority: {{ $index + 1 }}</strong>
                                <ul>
                                    @for (partition of priority; track partition.id) {
                                        <li>{{ partition.name }}</li>
                                    }
                                </ul>
                            </li>
                        }
                    </ul>
                </dd>
            }

            @if (data().sectionType; as sectionType) {
                <dt>Section Type:</dt>
                <dd>{{ sectionType }}</dd>
            }

            @if (data().subjectCode; as subjectCode) {
                <dt>Subject Code:</dt>
                <dd>{{ subjectCode }}</dd>
            }

            @if (data().subTerm; as subTerm) {
                <dt>Subterm:</dt>
                <dd>{{ subTerm }}</dd>
            }

            @if (data().courseReferenceNumber; as courseReferenceNumber) {
                <dt>Course Reference Number:</dt>
                <dd>{{ courseReferenceNumber }}</dd>
            }

            <dt>Reference:</dt>
            <dd>{{ data().reference }}</dd>

            @if (item()?.virtualGrid?.overlappingItems()?.length) {
                <dt>Overlapping Sections:</dt>
                <dd>
                    <ul>
                        @for (item of item()!.virtualGrid.overlappingItems(); track item.id) {
                            <li>
                                <s25-ng-button [type]="'link'" (clicked)="bringToFront(item)" [title]="'Show'">
                                    {{ item.academicGrid.eventName }}
                                </s25-ng-button>
                            </li>
                        }
                    </ul>
                </dd>
            }

            @if (item()?.academicGrid?.dragMessage; as message) {
                <dt>Section Cannot Be Moved:</dt>
                <dd>{{ message }}</dd>
            }
        </dl>
    `,
    styles: `
        ::ng-deep .popover-body:has(s25-ng-academic-grid-item-tooltip) {
            padding: 0;
        }

        :host {
            display: block;
            border-top: 5px solid var(--color--objects-event, #2b7c42);
            padding: 1rem;
            border-radius: 3px;
        }

        .header {
            font-weight: 700;
            display: flex;
            gap: 0.5rem;
            justify-content: space-between;
            --icon-size: 2.5em;
        }

        ul {
            padding: 0;
            font-size: 0.9em;
            margin: 0;
            list-style-position: inside;
        }

        ul ul {
            padding-inline-start: 1rem;
            font-size: 1em;
        }

        dl {
            margin: 0;
        }

        dt {
            font-weight: normal;
            margin-top: 1rem;
            font-size: 0.75rem;
        }

        dd {
            margin: 0;
        }

        .headcounts dl,
        .additional-time dl {
            display: grid;
            grid-template-columns: auto 1fr;
            align-items: center;
            column-gap: 0.5rem;
            font-size: 0.9em;
        }

        .headcounts dt,
        .additional-time dt {
            font-style: italic;
            margin: 0;
        }

        .sortByRelevance {
            display: block;
            margin-top: 1rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25RouterLinkDirective, S25IconComponent, S25ButtonComponent, S25ItemComponent],
})
export class S25AcademicGridItemTooltipComponent {
    public readonly dataInput = input.required<{ data: AcademicGrid.EventTooltipData } | { item: AcademicGrid.Item }>({
        alias: "data",
    });

    protected readonly Item = Item;
    protected readonly store = inject(AcademicGridStore);
    private readonly userStore = inject(UserStore);

    protected item = linkedSignal(() => {
        const data = this.dataInput();
        return "item" in data ? data.item : undefined;
    });
    protected readonly data = computed(() => {
        if (this.item()) return this.item()!.academicGrid;
        return (<{ data: AcademicGrid.EventTooltipData }>this.dataInput()).data;
    });

    protected readonly eventTime = computed(() => {
        const current = this.data().current();
        const is24Hours = this.userStore.is24Hours();
        const start = S25Date.toTimeStrFromHours(current.eventStartHour, is24Hours);
        const end = S25Date.toTimeStrFromHours(current.eventEndHour, is24Hours);
        const range = `${start} - ${end}`;
        const dow = current.position.dow;
        return `${dow} ${range}`;
    });

    protected readonly additionalTime = computed(() => {
        const tooltip = this.data();
        const bands = [
            { label: OccurrenceTimeRangeLabels.setupTakedown.start, minutes: tooltip.setup },
            { label: OccurrenceTimeRangeLabels.prePost.start, minutes: tooltip.pre },
            { label: OccurrenceTimeRangeLabels.prePost.end, minutes: tooltip.post },
            { label: OccurrenceTimeRangeLabels.setupTakedown.end, minutes: tooltip.takedown },
        ];
        return bands
            .filter((band) => band.minutes > 0)
            .map((band) => ({
                label: band.label,
                duration: S25Date.printDurationMinutes(band.minutes),
            }));
    });

    protected readonly hasSortByRelevance = computed(() => {
        return this.item() && this.store.mode() === "location" && !this.data().isLocationConflict;
    });
    protected readonly sortedByRelevance = computed(
        () => this.hasSortByRelevance() && this.store.sortLocationsByItem() === this.item(),
    );

    protected toggleSortByRelevance() {
        this.store.sortLocationsByRelevance(this.sortedByRelevance() ? undefined : this.item());
    }

    protected bringToFront(item: AcademicGrid.Item): void {
        this.store.bringToFront(item);
        this.item.set(item);
    }
}
